"use client";

import { CommentEvents, trackEvent } from "@/analytics";
import { ChevronDown, Smile } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";

import { Comedian } from "@/data/comedians";
import { CommentAuthorComedian } from "@punchup/data/models";
import Image from "next/image";
import Picker from "@emoji-mart/react";
import { User } from "@supabase/supabase-js";
import data from "@emoji-mart/data";
import dayjs from "@punchup/utils/dayjs";
import { getCDNImageURL } from "@punchup/utils";
import { useFeatureGate } from "statsig-react";
import { useManagedComedians } from "@/hooks/useManagedComedians";
import { validateCommentContent } from "./utils/commentSecurity";

// Map a full Comedian record to the minimal author shape stored/rendered on a comment.
const toAuthorComedian = (c: Comedian): CommentAuthorComedian => ({
  id: c.id,
  display_name: c.display_name,
  display_name_short: c.display_name_short,
  about_img: c.about_img,
  slug: c.slug,
  is_punchup_verified: c.is_punchup_verified,
});

// Type for emoji-mart emoji selection
interface EmojiSelection {
  native: string;
  unified?: string;
  id?: string;
  name?: string;
  colons?: string;
  skin?: number;
}

interface CommentInputProps {
  onSubmit: (
    content: string,
    authorComedian?: CommentAuthorComedian | null
  ) => void;
  disabled?: boolean;
  placeholder?: string;
  autoFocus?: boolean;
  currentUser?: User | null;
  availableUsers?: Map<string, { id: string; username: string }>;
  variant?: "default" | "outlined"; // "outlined" for use within bordered containers
  // Optional tracking context
  specialId?: string;
  comedianId?: string;
  isLoggedIn?: boolean;
  userFollowsComedian?: boolean;
  isReply?: boolean;
  parentCommentId?: string;
}

export default function CommentInput({
  onSubmit,
  disabled = false,
  placeholder = "Add a comment...",
  autoFocus = false,
  currentUser,
  availableUsers = new Map(),
  variant = "default",
  specialId,
  comedianId,
  isLoggedIn,
  userFollowsComedian,
  isReply = false,
  parentCommentId,
}: CommentInputProps) {
  const [content, setContent] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [charCount, setCharCount] = useState(0);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const emojiPickerRef = useRef<HTMLDivElement>(null);

  // "Comment as the artist" identity selector (gated). Available to users who
  // manage one or more artists; lets a manager post as the artist instead of
  // as themselves. null = posting as the user (default).
  const { gate: artistCommentsGate } = useFeatureGate("artist_comments");
  const { comedians: managedComedians } = useManagedComedians();
  const canCommentAsArtist =
    artistCommentsGate?.value === true && managedComedians.length > 0;
  const [selectedComedianId, setSelectedComedianId] = useState<string | null>(
    null
  );
  const [showIdentityMenu, setShowIdentityMenu] = useState(false);
  const identityMenuRef = useRef<HTMLDivElement>(null);
  const selectedComedian = canCommentAsArtist
    ? (managedComedians.find((c) => c.id === selectedComedianId) ?? null)
    : null;

  const maxLength = 1000;
  const isOverLimit = charCount > maxLength;
  const isNearLimit = charCount > maxLength * 0.8;

  // Get user display info
  const getUserInfo = () => {
    if (!currentUser?.user_metadata) {
      return {
        displayName: currentUser?.email?.split("@")[0] || "User",
        avatarUrl: null,
      };
    }

    const metadata = currentUser.user_metadata;
    return {
      // Prioritize email prefix to match profile URLs, but keep rich metadata for avatar
      displayName:
        currentUser.email?.split("@")[0] ||
        metadata.full_name ||
        metadata.name ||
        "User",
      avatarUrl: metadata.avatar_url || metadata.picture || null,
    };
  };

  const userInfo = getUserInfo();

  // Track input focus
  const handleFocus = useCallback(() => {
    if (specialId && comedianId) {
      trackEvent(
        isReply
          ? CommentEvents.REPLY_INPUT_OPENED
          : CommentEvents.COMMENT_INPUT_FOCUSED,
        {
          specialId,
          comedianId,
          isLoggedIn,
          userFollowsComedian,
          isReply,
          parentCommentId,
        }
      );
    }
    setIsExpanded(true);
  }, [
    specialId,
    comedianId,
    isLoggedIn,
    userFollowsComedian,
    isReply,
    parentCommentId,
  ]);

  // Track input blur
  const handleBlur = useCallback(() => {
    if (specialId && comedianId && content.length === 0) {
      trackEvent(CommentEvents.COMMENT_INPUT_BLURRED, {
        specialId,
        comedianId,
        contentLength: content.length,
        timeSpentFocused: dayjs().valueOf(), // Could be enhanced with actual time tracking
        isLoggedIn,
        userFollowsComedian,
        isReply,
        parentCommentId,
      });
      setIsExpanded(false);
    }
  }, [
    specialId,
    comedianId,
    content.length,
    isLoggedIn,
    userFollowsComedian,
    isReply,
    parentCommentId,
  ]);

  // Combined content change handler with tracking
  const handleContentChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      const newContent = e.target.value;
      setContent(newContent);
      setCharCount(newContent.length);

      // Clear validation error on change
      if (validationError) {
        setValidationError(null);
      }

      // Auto-resize textarea
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
        textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
      }

      // Track typing activity (with basic debouncing)
      if (
        specialId &&
        comedianId &&
        newContent.length > 0 &&
        newContent.length % 10 === 0
      ) {
        trackEvent(CommentEvents.COMMENT_INPUT_TYPING, {
          specialId,
          comedianId,
          contentLength: newContent.length,
          isLoggedIn,
          userFollowsComedian,
          isReply,
          parentCommentId,
        });
      }
    },
    [
      specialId,
      comedianId,
      isLoggedIn,
      userFollowsComedian,
      isReply,
      parentCommentId,
      validationError,
    ]
  );

  const handleSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();

    if (disabled || !content.trim()) return;

    // Validate content
    const validation = validateCommentContent(content);
    if (!validation.isValid) {
      setValidationError(validation.errors.join(", "));

      // Track validation failure
      if (specialId && comedianId) {
        trackEvent(CommentEvents.COMMENT_VALIDATION_FAILED, {
          specialId,
          comedianId,
          validationErrors: validation.errors,
          contentLength: content.length,
          isLoggedIn,
          userFollowsComedian,
          isReply,
          parentCommentId,
        });
      }
      return;
    }

    setValidationError(null);
    onSubmit(
      content.trim(),
      selectedComedian ? toAuthorComedian(selectedComedian) : null
    );
    setContent("");
    setCharCount(0);
    setIsExpanded(false);
    setShowEmojiPicker(false);
    setShowIdentityMenu(false);

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  };

  const handleCancel = () => {
    setContent("");
    setCharCount(0);
    setIsExpanded(false);
    setValidationError(null);
    setShowEmojiPicker(false);

    // Reset textarea height
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const handleEmojiSelect = (emoji: EmojiSelection) => {
    const newContent = content + emoji.native;

    // Update state
    setContent(newContent);
    setCharCount(newContent.length);

    // Clear validation error
    if (validationError) {
      setValidationError(null);
    }

    // Update textarea and focus
    if (textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  // Close emoji picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(event.target as Node)
      ) {
        setShowEmojiPicker(false);
      }
    };

    if (showEmojiPicker) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showEmojiPicker]);

  // Close the identity menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        identityMenuRef.current &&
        !identityMenuRef.current.contains(event.target as Node)
      ) {
        setShowIdentityMenu(false);
      }
    };

    if (showIdentityMenu) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }
  }, [showIdentityMenu]);

  return (
    <div>
      {/* Avatar and input container */}
      <div className="flex items-start gap-3">
        {/* Author Avatar — reflects the selected identity (self or artist) */}
        <div className="mt-2">
          {selectedComedian ? (
            <div className="w-10 h-10 overflow-hidden rounded-full bg-black flex items-center justify-center">
              {selectedComedian.about_img ? (
                <Image
                  src={getCDNImageURL(selectedComedian.about_img)}
                  alt={selectedComedian.display_name}
                  width={40}
                  height={40}
                  className="h-full w-full object-cover"
                />
              ) : (
                <span className="text-white font-medium">
                  {selectedComedian.display_name[0]?.toUpperCase()}
                </span>
              )}
            </div>
          ) : userInfo.avatarUrl ? (
            <Image
              src={userInfo.avatarUrl}
              alt={userInfo.displayName}
              width={28}
              height={28}
              className="rounded-full"
            />
          ) : (
            <div className="w-10 h-10 bg-gradient-to-br from-blue-400 to-purple-500 rounded-full flex items-center justify-center text-white font-medium">
              {userInfo.displayName[0]?.toUpperCase()}
            </div>
          )}
        </div>

        {/* Input area */}
        <div className="flex-1">
          {/* "Comment as" identity selector — only for users who manage artists */}
          {canCommentAsArtist && (
            <div className="relative mb-1 inline-block" ref={identityMenuRef}>
              <button
                type="button"
                onClick={() => setShowIdentityMenu((prev) => !prev)}
                className="flex items-center gap-1 text-xs font-medium text-gray-600 hover:text-gray-900 transition-colors"
              >
                <span>
                  Commenting as{" "}
                  <span className="font-semibold">
                    {selectedComedian
                      ? selectedComedian.display_name
                      : "yourself"}
                  </span>
                </span>
                <ChevronDown className="w-3.5 h-3.5" />
              </button>

              {showIdentityMenu && (
                <div className="absolute left-0 top-6 z-30 max-h-60 min-w-[200px] overflow-y-auto overscroll-contain bg-white border border-gray-200 rounded-lg shadow-lg py-1">
                  <button
                    type="button"
                    onClick={() => {
                      setSelectedComedianId(null);
                      setShowIdentityMenu(false);
                    }}
                    className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full text-left"
                  >
                    Yourself ({userInfo.displayName})
                  </button>
                  {managedComedians.map((comedian) => (
                    <button
                      key={comedian.id}
                      type="button"
                      onClick={() => {
                        setSelectedComedianId(comedian.id);
                        setShowIdentityMenu(false);
                      }}
                      className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 w-full text-left"
                    >
                      {comedian.about_img && (
                        <span className="w-6 h-6 overflow-hidden rounded-full bg-black shrink-0">
                          <Image
                            src={getCDNImageURL(comedian.about_img)}
                            alt={comedian.display_name}
                            width={24}
                            height={24}
                            className="h-full w-full object-cover"
                          />
                        </span>
                      )}
                      <span className="truncate">{comedian.display_name}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          <form onSubmit={handleSubmit} className="w-full">
            <div className="relative">
              <textarea
                ref={textareaRef}
                value={content}
                onChange={handleContentChange}
                onFocus={handleFocus}
                onBlur={handleBlur}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
                disabled={disabled}
                autoFocus={autoFocus}
                maxLength={maxLength}
                className={
                  variant === "outlined"
                    ? `w-full px-0 py-2 resize-none focus:outline-none transition-colors overflow-hidden border-none ${
                        disabled
                          ? "opacity-50 cursor-not-allowed"
                          : "cursor-text"
                      }`
                    : `w-full px-0 py-2 border-b resize-none focus:outline-none transition-colors overflow-hidden ${
                        isExpanded ? "border-gray-900" : "border-gray-300"
                      } ${validationError || isOverLimit ? "border-red-500" : ""} ${
                        disabled
                          ? "opacity-50 cursor-not-allowed"
                          : "cursor-text"
                      }`
                }
                rows={1}
                style={{ minHeight: "40px" }}
              />
            </div>

            {/* Expanded actions */}
            {isExpanded && (
              <div className="mt-4 space-y-3">
                {/* Validation error */}
                {validationError && (
                  <div className="text-sm text-red-600 flex items-center gap-1">
                    <span className="font-medium">Error:</span>
                    <span>{validationError}</span>
                  </div>
                )}

                {/* Character count */}
                {content.length > 0 && (
                  <div className="text-xs text-gray-500">
                    <span
                      className={
                        isOverLimit
                          ? "text-red-600 font-semibold"
                          : isNearLimit
                            ? "text-yellow-600"
                            : ""
                      }
                    >
                      {charCount}/{maxLength}
                    </span>
                  </div>
                )}

                {/* Action buttons */}
                <div className="flex items-center justify-between">
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                      className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                      aria-label="Add emoji"
                    >
                      <Smile className="w-5 h-5 text-gray-600" />
                    </button>

                    {/* Emoji Picker */}
                    {showEmojiPicker && (
                      <div
                        ref={emojiPickerRef}
                        className="absolute bottom-full left-0 mb-2 z-50"
                      >
                        <Picker
                          data={data}
                          onEmojiSelect={handleEmojiSelect}
                          theme="light"
                          previewPosition="none"
                          skinTonePosition="none"
                          maxFrequentRows={2}
                          perLine={8}
                          emojiSize={20}
                          emojiButtonSize={28}
                        />
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={handleCancel}
                      className="px-4 py-1.5 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={
                        !content.trim() ||
                        disabled ||
                        isOverLimit ||
                        !!validationError
                      }
                      className="px-4 py-1.5 text-sm bg-orange-500 text-white rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                    >
                      Comment
                    </button>
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}
